/**
 * Zone-Based Redux Selectors Hook
 * 
 * This hook replaces the old useReduxSelectors.js with zone-based architecture.
 * It provides a unified interface to access all zone data while maintaining
 * backward compatibility where possible.
 * 
 * Migration from: useReduxSelectors.js
 * Part of: Full Zone Migration
 */

import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { useSharedData, useCharacterZone, useVariationZone } from './useZoneHooks';

export const useReduxSelectorsZoneBased = () => {
  // Use zone-based hooks
  const sharedData = useSharedData();
  const characterZone = useCharacterZone();
  const variationZone = useVariationZone();

  // Extract from shared data
  const {
    litGroups,
    books,
    litGroupProfiles,
    bookProfiles,
    selections,
    loading: sharedLoading,
    errors: sharedErrors,
    lastUpdated: sharedLastUpdated
  } = sharedData;

  // Destructure selections for easier access
  const {
    litGroupId,
    bookId,
    characterId,
    variationId
  } = selections;

  // Find selected entities
  const selectedLitGroup = useMemo(() =>
    litGroups.find(lg => lg.uuid === litGroupId) || null,
    [litGroups, litGroupId]
  );

  const selectedLitGroupProfile = useMemo(() =>
    litGroupProfiles[litGroupId] || null,
    [litGroupProfiles, litGroupId]
  );

  const selectedBook = useMemo(() =>
    books.find(book => book.uuid === bookId) || null,
    [books, bookId]
  );

  const selectedBookProfile = useMemo(() =>
    bookProfiles[bookId] || null,
    [bookProfiles, bookId]
  );

  // Get filtered collections based on selections
  const booksByLitGroup = useMemo(() =>
    litGroupId ? books.filter(book => book.litGroupId === litGroupId) : [],
    [books, litGroupId]
  );

  const charactersByBook = useMemo(() =>
    bookId ? characterZone.characters.filter(char => char.bookId === bookId) : [],
    [characterZone.characters, bookId]
  );

  const variationsByCharacter = useMemo(() =>
    characterId ? variationZone.variations.filter(v => v.characterId === characterId) : [],
    [variationZone.variations, characterId]
  );

  // Combine loading states
  const isLoading = {
    litGroups: sharedLoading.litGroups,
    books: sharedLoading.books,
    characters: characterZone.loading,
    variations: variationZone.loading,
    profiles: sharedLoading.profiles
  };

  // Combine error states
  const errors = {
    litGroups: sharedErrors.litGroups,
    books: sharedErrors.books,
    characters: characterZone.error,
    variations: variationZone.error,
    profiles: sharedErrors.profiles
  };

  // Last updated timestamps
  const lastUpdated = {
    litGroups: sharedLastUpdated.litGroups,
    books: sharedLastUpdated.books,
    characters: characterZone.lastUpdated,
    variations: variationZone.lastUpdated,
    profiles: sharedLastUpdated.profiles
  };

  // Form states (from variation zone)
  const formState = {
    character: {
      isDirty: variationZone.hasUnsavedChanges,
      isSubmitting: variationZone.isSubmitting,
      errors: variationZone.error
    }
  };

  return {
    // Selection IDs
    litGroupId,
    bookId,
    characterId,
    variationId,

    // Selected entities
    selectedLitGroup,
    selectedLitGroupProfile,
    selectedBook,
    selectedBookProfile,
    selectedCharacter: characterZone.selectedCharacter,
    selectedVariation: variationZone.activeVariation,

    // Collections
    litGroups,
    books,
    booksByLitGroup,
    characters: characterZone.characters,
    charactersByBook,
    variations: variationZone.variations,
    variationsByCharacter,

    // Loading states (individual)
    litGroupsLoading: isLoading.litGroups,
    booksLoading: isLoading.books,
    charactersLoading: isLoading.characters,
    variationsLoading: isLoading.variations,
    profilesLoading: isLoading.profiles,

    // Loading states (combined)
    isLoading,

    // Error states (individual)
    litGroupsError: errors.litGroups,
    booksError: errors.books,
    charactersError: errors.characters,
    variationsError: errors.variations,
    profilesError: errors.profiles,

    // Error states (combined)
    errors,

    // Last updated
    lastUpdated,

    // Form state
    formState,
    characterFormState: formState.character,

    // Zone-specific data
    variationCounts: variationZone.variationCounts,
    activeVersion: variationZone.activeVersion,
    samples: variationZone.samples,
    editMode: variationZone.editMode,

    // Direct zone access
    sharedData,
    characterZone,
    variationZone
  };
};

// Export as default for compatibility
export default useReduxSelectorsZoneBased;

// Also export with old name for easier migration
export const useReduxSelectors = useReduxSelectorsZoneBased;
